// T060 -- Trading event definitions for tx engine analytics.

import type { EventDefinition } from './navigation';

// Property types for each trading event.

export type TxStartedProperties = {
  traceId: string;
  instruction: string;
  computeUnits: number;
  walletType: string;
  /** Which signing path is used: 'external' | 'turnkey-client' | 'turnkey-backend' */
  signingMethod: string;
  network: string;
  slippageBps: number;
  suggestedSlippageBps: number;
  marketPhase: string;
};

export type TxSimFailedProperties = {
  traceId: string;
  error: string;
};

export type TxSimTimeoutProperties = {
  traceId: string;
};

export type TxPriorityFeeFetchedProperties = {
  traceId: string;
  fee: number;
  percentile: string;
};

export type TxPriorityFeeFallbackProperties = {
  traceId: string;
  floor: number;
  reason: string;
};

export type TxSignStartedProperties = {
  traceId: string;
  walletType: string;
};

export type TxSignFailedProperties = {
  traceId: string;
  error: string;
  walletType: string;
  /** Which signing path failed: 'external' | 'turnkey-client' | 'turnkey-backend' */
  signingMethod: string;
  /** Allowlisted frame from `describeSignFailure`: never the raw message. */
  errorClass?: string;
  errorName?: string;
  errorCode?: string;
  statusCode?: number;
};

export type TxRelayAcceptedProperties = {
  traceId: string;
  signature: string;
  latencyMs: number;
  // Spec 056 / T107: phase telemetry on every submission. Optional —
  // fired only on the relay paths (failover / primary). Better Stack
  // dashboards continue reading `tx_relay_accepted`; the new fields
  // are additive.
  phase_pre_relay_build_ms?: number;
  phase_relay_roundtrip_ms?: number;
  phase_confirmation_ms?: number;
  phase_total_ms?: number;
  txPath?: 'relay-failover' | 'relay-primary';
};

export type TxRelayFailedProperties = {
  traceId: string;
  errorCode: string;
  /** Spec 056 / coverage gap C3 — relay path that failed. */
  txPath: 'relay-failover' | 'relay-primary';
};

export type TxConfirmSuccessProperties = {
  traceId: string;
  signature: string;
  slot: number;
  latencyMs: number;
  /** Spec 056 / coverage gap C2 — origin path of this confirmation. */
  txPath: 'turnkey-sponsored' | 'relay-failover' | 'relay-primary';
};

export type TxConfirmFailedProperties = {
  traceId: string;
  signature: string;
  error: string;
  /** Spec 056 / coverage gap C2 — origin path of this confirmation. */
  txPath: 'turnkey-sponsored' | 'relay-failover' | 'relay-primary';
};

export type TxBlockhashExpiredProperties = {
  traceId: string;
  attempt: number;
};

export type TxMaxRetriesProperties = {
  traceId: string;
  attempts: number;
};

export type TxCancelledProperties = {
  traceId: string;
  stage: string;
};

// Spec 056 — three-path Turnkey/relay routing.

export type TxSubmitAttemptedProperties = {
  traceId: string;
  walletType: 'embedded' | 'imported' | 'external';
  txPath: 'turnkey-sponsored' | 'relay-failover' | 'relay-primary';
  instructionName: string;
};

export type TxTurnkeySendCompletedProperties = {
  traceId: string;
  signature: string;
  walletAddress: string;
  providerActivityIdHash: string;
  /** Time from solSendTransaction send to its initial response. */
  phase_solSendTransaction_roundtrip_ms: number;
  /** Time from solSendTransaction send to first non-null Solana signature. */
  phase_signature_available_ms: number;
  /** Time from signature handoff to confirmation pipeline returning. */
  phase_confirmation_ms: number;
  /** Wall-clock from submitTransaction entry to result return. */
  phase_total_ms: number;
};

export type TurnkeyProviderErrorCode =
  | 'NO_SESSION_FOUND'
  | 'PERMISSION_DENIED'
  | 'RATE_LIMIT_EXCEEDED'
  | 'REQUEST_INVALID'
  | 'SESSION_EXPIRED'
  | 'SIGNING_QUOTA_EXCEEDED';

export type TxTurnkeyFailoverTriggeredProperties = {
  traceId: string;
  instructionName: string;
  triggerReason:
    | 'forced_relay'
    | 'additional_presigner'
    | 'idempotent_ata_create'
    | 'mid_flight_quota_exceeded'
    | 'turnkey_failed'
    | 'not_configured'
    | 'local_rpc'
    | 'cap_exhausted';
  /** Allowlisted Turnkey error code when failover was triggered mid-flight. */
  turnkeyErrorCode?: TurnkeyProviderErrorCode;
  providerActivityIdHash?: string;
};

/**
 * Spec 056 — Turnkey poll loop encountered an error (rate-limit retried,
 * timeout, network glitch, or terminal failure). Distinct from
 * `tx_turnkey_failover_triggered` because the poll error may itself
 * resolve via internal backoff (RATE_LIMIT_EXCEEDED) and never reach
 * the failover branch.
 */
export type TxTurnkeyPollErrorProperties = {
  traceId: string;
  providerErrorCode?: TurnkeyProviderErrorCode;
  providerActivityIdHash?: string;
  buildDurationMs?: number;
  submitDurationMs?: number;
  totalStatusRequests?: number;
  successfulStatusResponses?: number;
  rateLimitResponses?: number;
  transportFailures?: number;
  /** Whether the engine treats this poll error as failover-eligible. */
  willFailover: boolean;
};

export type TxSignatureObservedProperties = {
  traceId: string;
  providerActivityIdHash: string;
  signature: string;
  signatureAvailableMs: number;
  totalStatusRequests: number;
  successfulStatusResponses: number;
  rateLimitResponses: number;
  transportFailures: number;
};

// Event definitions.

export const TX_STARTED: EventDefinition<TxStartedProperties> = {
  name: 'tx_started',
};

export const TX_SIM_FAILED: EventDefinition<TxSimFailedProperties> = {
  name: 'tx_simulation_failed',
};

export const TX_SIM_TIMEOUT: EventDefinition<TxSimTimeoutProperties> = {
  name: 'tx_simulation_timeout',
};

export const TX_PRIORITY_FEE_FETCHED: EventDefinition<TxPriorityFeeFetchedProperties> = {
  name: 'tx_priority_fee_fetched',
};

export const TX_PRIORITY_FEE_FALLBACK: EventDefinition<TxPriorityFeeFallbackProperties> = {
  name: 'tx_priority_fee_fallback',
};

export const TX_SIGN_STARTED: EventDefinition<TxSignStartedProperties> = {
  name: 'tx_sign_started',
};

export const TX_SIGN_FAILED: EventDefinition<TxSignFailedProperties> = {
  name: 'tx_sign_failed',
};

export const TX_RELAY_ACCEPTED: EventDefinition<TxRelayAcceptedProperties> = {
  name: 'tx_relay_accepted',
};

export const TX_RELAY_FAILED: EventDefinition<TxRelayFailedProperties> = {
  name: 'tx_relay_failed',
};

export const TX_CONFIRM_SUCCESS: EventDefinition<TxConfirmSuccessProperties> = {
  name: 'tx_confirm_success',
};

export const TX_CONFIRM_FAILED: EventDefinition<TxConfirmFailedProperties> = {
  name: 'tx_confirm_failed',
};

export const TX_BLOCKHASH_EXPIRED: EventDefinition<TxBlockhashExpiredProperties> = {
  name: 'tx_blockhash_expired',
};

export const TX_MAX_RETRIES: EventDefinition<TxMaxRetriesProperties> = {
  name: 'tx_max_retries',
};

export const TX_CANCELLED: EventDefinition<TxCancelledProperties> = {
  name: 'tx_cancelled',
};

// Spec 056 — Turnkey routing telemetry.

export const TX_SUBMIT_ATTEMPTED: EventDefinition<TxSubmitAttemptedProperties> = {
  name: 'tx_submit_attempted',
};

export const TX_TURNKEY_SEND_COMPLETED: EventDefinition<TxTurnkeySendCompletedProperties> = {
  name: 'tx_turnkey_send_completed',
};

export const TX_TURNKEY_FAILOVER_TRIGGERED: EventDefinition<TxTurnkeyFailoverTriggeredProperties> =
  {
    name: 'tx_turnkey_failover_triggered',
  };

export const TX_SIGNATURE_OBSERVED: EventDefinition<TxSignatureObservedProperties> = {
  name: 'tx_signature_observed',
};

export const TX_TURNKEY_POLL_ERROR: EventDefinition<TxTurnkeyPollErrorProperties> = {
  name: 'tx_turnkey_poll_error',
};

export type TxEstimatePriceChangedProperties = {
  traceId: string;
  deviationBps: number;
  slippageBps: number;
};

export type TxEstimateHighPriceImpactProperties = {
  traceId: string;
  priceImpactBps: number;
  direction: string;
  marketPhase: string;
};

export const TX_ESTIMATE_PRICE_CHANGED: EventDefinition<TxEstimatePriceChangedProperties> = {
  name: 'tx_estimate_price_changed',
};

export const TX_ESTIMATE_HIGH_PRICE_IMPACT: EventDefinition<TxEstimateHighPriceImpactProperties> = {
  name: 'tx_estimate_high_price_impact',
};

// Trade-level summary events (fired from mutation layer with full context).
export type TradeChartMetric = 'market_share' | 'market_cap' | 'price' | 'none' | 'unknown';

export type TradeAttemptedProperties = {
  traceId: string;
  instruction: 'buy' | 'sell' | 'swap';
  walletType: string;
  /** Public on-chain wallet address used to join product events with indexed trades. */
  walletAddress: string;
  network: string;
  /** Public on-chain market address used to join product events with indexed markets. */
  marketAddress: string;
  outcomeIndex: number;
  entryPoint?: string;
  /** Selected market-detail chart when this trade was submitted. */
  chartMetricAtSubmission?: TradeChartMetric;
};

export type TradeSuccessProperties = {
  traceId: string;
  instruction: string;
  signature: string;
  walletType: string;
  walletAddress: string;
  latencyMs: number;
  network: string;
  marketAddress: string;
  outcomeIndex: number;
  /** Submission-time chart, retained even if confirmation arrives after a metric change. */
  chartMetricAtSubmission?: TradeChartMetric;
  /** Mobile outcome mint, when the native trade surface emits the event. */
  outcomeId?: string;
  /** Exact native trade amount in D6; swap events use `percentageBps`. */
  amountD6?: string;
  percentageBps?: number;
  /**
   * Origin surface that initiated this trade. Used by SC-007 (portfolio sell
   * deep-link adoption) and other funnel attributions. Optional — emitter
   * sets it when a known entry-point signature is detectable; absence means
   * the user opened the trade form directly.
   *
   * Known values:
   * - `'portfolio_sell_deeplink'` — set when `?action=sell&amount=max` is in
   *   the URL at emit time (spec 083 / SC-007).
   */
  entryPoint?: string;
  /** Trade notional in USDC D6 (exact string). Buy: wallet debit; sell/swap: gross proceeds. */
  tradeNotionalD6?: string;
  /** Trade notional as a summable USD number (analytics aggregation only). */
  tradeNotionalUsd?: number;
  /** Platform's share of the base trading fee in USDC D6 (exact string). '0' on AMM. */
  platformFeeD6?: string;
  /** Platform fee as a summable USD number — the per-trade revenue metric for LTV. */
  platformFeeUsd?: number;
  /**
   * 1-based ordinal of this trade for this user, so activation is the exact
   * filter `userTradeNumber = 1` ("new activated users" north star). Derived
   * client-side with no fetch and scoped to the device — see
   * `lib/analytics/trade-lifecycle`. Absent when the ordinal is unknown, which
   * must never be read as "first".
   */
  userTradeNumber?: number;
};

export type TradeErrorProperties = {
  traceId: string;
  instruction: string;
  walletType: string;
  walletAddress: string;
  errorCode: string;
  stage: string;
  latencyMs: number;
  network: string;
  marketAddress: string;
  outcomeIndex: number;
  outcomeId?: string;
  morfiError: string;
  recoveryAction: string;
  errorClass: string;
};

export const TRADE_ATTEMPTED: EventDefinition<TradeAttemptedProperties> = {
  name: 'trade_attempted',
};

export const TRADE_SUCCESS: EventDefinition<TradeSuccessProperties> = {
  name: 'trade_success',
};

export const TRADE_ERROR: EventDefinition<TradeErrorProperties> = {
  name: 'trade_error',
};

// RUM action names for Datadog custom actions.
export const TRANSACTION_SUBMITTED = 'transaction_submitted';
export const TRANSACTION_CONFIRMED = 'transaction_confirmed';
export const TRANSACTION_FAILED = 'transaction_failed';
